import { useState, useEffect, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { api } from "services/api.service";
import { LogisticsPartner } from "types/logisticsPartner";

type LogisticsPartnerProps = {
  name?: string;
  type?: string;
};

export default function useLogisticsPartners(defaultPageSize?: number) {
  const location = useLocation();
  const params = useParams();
  const [logisticsPartners, setLogisticsPartners] = useState<LogisticsPartner[]>([]);
  const [logisticsPartner, setLogisticsPartner] = useState<LogisticsPartner>();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const { search } = useLocation();
  const [isLoading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [partnerLoading, setPartnerLoading] = useState(false);

  const getLogisticsPartner = useCallback(() => {
    setPartnerLoading(true);
    api
      .get(`/logistics_partners/${params.uuid}`)
      .then(({ data: { logisticsPartner: fetchedLogisticsPartner } }) => {
        setLogisticsPartner(fetchedLogisticsPartner);
      })
      .finally(() => {
        setPartnerLoading(false);
      });
  }, [params.uuid]);

  const getLogisticsPartners = useCallback(() => {
    const searchHash = new URLSearchParams(location.search);
    if (!searchHash.get("page[size]") && defaultPageSize) {
      searchHash.append("page[size]", String(defaultPageSize));
    }
    api.get(`/logistics_partners?${searchHash.toString()}`).then(
      ({
        data: {
          logisticsPartners: fetchedLogisticsPartners,
          meta: { totalPages: fetchedTotalPages, count: fetchedCount },
        },
      }) => {
        setLogisticsPartners(fetchedLogisticsPartners);
        setTotalPages(fetchedTotalPages);
        setCount(fetchedCount);
      },
    );
  }, [location.search, defaultPageSize]);

  const createLogisticsPartner = (newLogisticsPartner: LogisticsPartnerProps) => {
    setLoading(true);
    api
      .post(`/logistics_partners`, {
        logisticsPartner: newLogisticsPartner,
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const updateLogisticsPartner = (updatedLogisticsPartner: LogisticsPartnerProps) => {
    setLoading(true);
    api
      .put(`/logistics_partners/${params.uuid}`, {
        logisticsPartner: updatedLogisticsPartner,
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (params.uuid === undefined) {
      getLogisticsPartners();
    }
  }, [getLogisticsPartners, params.uuid, search]);

  useEffect(() => {
    if (params.uuid) {
      getLogisticsPartner();
    }
  }, [params.uuid, location.pathname, getLogisticsPartner]);

  return {
    logisticsPartners,
    logisticsPartner,
    createLogisticsPartner,
    updateLogisticsPartner,
    totalPages,
    count,
    isLoading,
  };
}
